import { memo } from 'react';
import useTween from './useTween';

const VIEWBOX = 200;
const CENTRE = VIEWBOX / 2;

const DOT_RING_RADII = [80, 88, 96];
const DOTS_PER_RING = 60; 
const DOT_RADIUS = 2.7;

const ARC_RADIUS = 64;
const ARC_WIDTH = 14;
const ARC_CIRCUMFERENCE = 2 * Math.PI * ARC_RADIUS;

const TRACK_COLOR = '#D2D2D7';

const DOT_POSITIONS = DOT_RING_RADII.flatMap((radius) =>
    Array.from({ length: DOTS_PER_RING }, (unused, i) => {
        // -90deg puts index 0 at 12 o'clock.
        const angle = ((i / DOTS_PER_RING) * 360 - 90) * (Math.PI / 180);

        return {
            key: `${radius}-${i}`,
            index: i,
            cx: CENTRE + radius * Math.cos(angle),
            cy: CENTRE + radius * Math.sin(angle)
        };
    })
);

const clampPercent = (value) => {
    const number = Number(value);
    if (!isFinite(number) || number <= 0) return 0;
    return Math.min(number, 100);
};

const DonutDots = memo(function DonutDots({ count, color }) {
    return DOT_POSITIONS.map((dot) => (
        <circle
            key={dot.key}
            cx={dot.cx}
            cy={dot.cy}
            r={DOT_RADIUS}
            fill={dot.index < count ? color : TRACK_COLOR}
        />
    ));
});

function Donut({ percent, color, caption, approximate = true }) {
    const target = clampPercent(percent);
    const pct = useTween(target);

    const rounded = Math.round(pct);
    const display = rounded > 0 && approximate ? `~${rounded}%` : `${rounded}%`;

    return (
        <svg className='corp-donut' viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} role='img'
             aria-label={`${Math.round(target)}% ${caption || ''}`.trim()}>
            <DonutDots count={Math.round((pct / 100) * DOTS_PER_RING)} color={color} />

            <circle
                cx={CENTRE}
                cy={CENTRE}
                r={ARC_RADIUS}
                fill='none'
                stroke={TRACK_COLOR}
                strokeWidth={ARC_WIDTH}
            />
            <circle
                cx={CENTRE}
                cy={CENTRE}
                r={ARC_RADIUS}
                fill='none'
                stroke={color}
                strokeWidth={ARC_WIDTH}
                strokeDasharray={ARC_CIRCUMFERENCE}
                strokeDashoffset={ARC_CIRCUMFERENCE * (1 - pct / 100)}
                transform={`rotate(-90 ${CENTRE} ${CENTRE})`}
            />

            <text className='corp-donut__value' x={CENTRE} y={CENTRE} textAnchor='middle'>
                {display}
            </text>
            {caption && (
                <text className='corp-donut__caption' x={CENTRE} y={CENTRE + 20} textAnchor='middle'>
                    {caption}
                </text>
            )}
        </svg>
    );
}

export default memo(Donut);
